import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './onboardingTour.css';

export const TOUR_PENDING_KEY = 'pendingTour';
export const DUP_MERGE_EVENT  = 'tour:open-dup-merge';

const SHOW_BUTTONS = ['next', 'previous', 'close'];

// ── Full What's New tour (3 stages) ─────────────────────────────────────────

export function startTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        popover: {
          title: 'New Navigation Menu (1 of 3)',
          description:
            'Your navigation menu is now open in the top-right corner. All navigation has moved into this single menu — Product Search, Admin tools, your account, and more — all organized into sections.',
        },
      },
      {
        element: '#tour-header-search',
        onHighlightStarted: () => setMenuOpen(false),
        popover: {
          title: 'Banner Search Bar (1 of 3)',
          description:
            'The search bar in the banner lets you search your product catalog from anywhere in the app without leaving the current page.',
          side: 'bottom',
          nextBtnText: 'Next: QR Styling →',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'qr');
            history.push('/settings');
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  d.drive();
}

export function resumeQrStage(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-qr-settings',
        popover: {
          title: 'QR Code Settings (2 of 3)',
          description:
            'All QR code configuration lives here. Create named style presets that users can choose at print time.',
          side: 'right',
        },
      },
      {
        element: '#tour-qr-styles-btn',
        popover: {
          title: 'Edit QR Styles (2 of 3)',
          description:
            'Click "Edit QR Styles" to open the style editor where you can create and manage style presets.',
          side: 'bottom',
          nextBtnText: 'Open Style Editor →',
          onNextClick: () => {
            d.destroy();
            document.getElementById('tour-qr-styles-btn')?.click();
            setTimeout(() => startQrStyleSubTour(history), 400);
          },
        },
      },
    ],
  });
  d.drive();
}

function startQrStyleSubTour(history) {
  let d2;
  d2 = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    overlayOpacity: 0,
    steps: [
      {
        popover: {
          title: 'QR Style: Appearance (2 of 3)',
          description:
            'Set the QR color and background, pick from six dot styles and corner shapes, upload a logo to appear in the center, and choose which product fields print as label text above and below the QR code. A live preview updates in real time.',
        },
      },
      {
        popover: {
          title: 'QR Style: Price Color Rules (2 of 3)',
          description:
            'Scroll down to "Price Colors" to add rules that automatically change a QR code\'s color based on the product\'s price range — so budget, mid-range, and premium products each show a different colored label.',
          nextBtnText: 'Next: Duplicate Detection →',
          onNextClick: () => {
            d2.destroy();
            resumeDuplicateStage(history);
          },
        },
      },
    ],
  });
  d2.drive();
}

// Stage 3 entry — navigates to /tour/products, driver runs on mount
export function resumeDuplicateStage(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-dup-product');
  history.push('/tour/products');
}

// ── Dup warning driver — called from TourProductsPage on mount ───────────────
// afterDone: callback to run after the merge dialog tour ends (null = go to /home)

export function driverDupWarning(history, afterDone) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-dup-warning-icon',
        popover: {
          title: 'Duplicate Detection (3 of 3)',
          description:
            'When a product has a potential duplicate in the catalog, an orange warning icon appears in the product list. The tooltip shows the match confidence. Click the icon to review the two products side by side.',
          side: 'right',
          nextBtnText: 'Review this duplicate →',
          onNextClick: () => {
            d.destroy();
            window.dispatchEvent(new CustomEvent(DUP_MERGE_EVENT, { detail: { afterDone } }));
          },
        },
      },
    ],
  });
  d.drive();
}

// ── Merge dialog driver — called from TourProductsPage after dialog opens ────
// afterDone is always provided (TourProductsPage wraps it to also close the dialog)

export function driverDupMerge(history, afterDone) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    overlayOpacity: 0,
    steps: [
      {
        popover: {
          title: 'Review Both Products (3 of 3)',
          description:
            'Both versions of the product are shown side by side with a match score for each field. Click "Keep this record" on the version you want to preserve — usually the one with the most complete data or the most recent update.',
        },
      },
      {
        popover: {
          title: 'Merge or Dismiss (3 of 3)',
          description:
            'Once you have selected a record to keep, click Confirm Merge to combine them into a single product. If these are actually different products, click Not a Duplicate to dismiss the match without making any changes.',
          nextBtnText: 'Continue Tour →',
          onNextClick: () => {
            d.destroy();
            afterDone();
          },
        },
      },
    ],
  });
  d.drive();
}

// ── Printed-indicator driver — called from TourProductsPage on mount ─────────

export function driverPrintedIndicator(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-printed-icon',
        popover: {
          title: 'Printed Product Indicator',
          description:
            'Products that have had a QR code label printed show a blue printer icon next to their name. Hover over it to confirm a label has been generated — so your team always knows which items are already tagged on the floor.',
          side: 'right',
          nextBtnText: 'Got it',
          onNextClick: () => {
            d.destroy();
            history.push('/products');
          },
        },
      },
    ],
  });
  d.drive();
}

// ── Individual feature tours (from What's New modal "Tour →" buttons) ────────

export function startNavTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        popover: {
          title: 'New Navigation Menu',
          description:
            'Your navigation menu is now in the top-right corner. Product Search is featured at the top, and everything else is organized into Navigate, Admin, and Account sections.',
          nextBtnText: 'Got it',
          onNextClick: () => { d.destroy(); setMenuOpen(false); },
        },
      },
    ],
  });
  setMenuOpen(true);
  d.drive();
}

export function startHeaderSearchTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-header-search',
        onHighlightStarted: () => setMenuOpen(false),
        popover: {
          title: 'Banner Search Bar',
          description:
            'The search bar in the top banner lets you search your product catalog from anywhere in the app without navigating away from the current page.',
          side: 'bottom',
          nextBtnText: 'Got it',
          onNextClick: () => d.destroy(),
        },
      },
    ],
  });
  d.drive();
}

export function startQrTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'qr');
  history.push('/settings');
}

export function resumePrintedIndicatorTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-printed');
  history.push('/tour/products');
}

// Discontinue a style / single option. The real controls only exist inside an
// expanded search result, so this runs against the mock product page (same
// approach as the printed-indicator and duplicate tours) where the expanded panel
// is always on screen.
export function startDiscontinueTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-discontinue');
  history.push('/tour/products');
}

export function driverDiscontinue(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-mock-expand-row',
        popover: {
          title: 'Discontinue (1 of 3)',
          description:
            'Everything starts by expanding a search result with the arrow next to the product. That opens the panel below, listing every option (color/size/distributor) the product sells in.',
          side: 'right',
        },
      },
      {
        element: '#tour-mock-discontinue-product',
        popover: {
          title: 'Discontinue the Whole Product (2 of 3)',
          description:
            'One click retires every option of the product, effective today. It stops showing as available in the showroom but stays in search, and the button flips to "Reactivate Product" so you can put it back.',
          side: 'bottom',
        },
      },
      {
        element: '#tour-mock-discontinue-option',
        popover: {
          title: 'Discontinue One Option (3 of 3)',
          description:
            'Only one color or size going away? Use the Discontinue button on that row instead and the rest of the product keeps selling. Discontinued options show a marker with the date and a Reactivate button. Admins and read/write users can do both.',
          side: 'left',
          nextBtnText: 'Got it',
          onNextClick: () => {
            d.destroy();
            history.push('/search');
          },
        },
      },
    ],
  });
  d.drive();
}

// Bulk retail price overrides. Runs against the mock page for the same reason as
// the discontinue tour — the bar only exists inside an expanded row in Edit mode.
export function startBulkPriceTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-bulk-price');
  history.push('/tour/products');
}

export function driverBulkPrice(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-mock-bulk-price',
        popover: {
          title: 'Price Every SKU at Once',
          description:
            'Expand a product, click "Edit Prices", and this bar appears. Type one price and "Apply to all" fills the override for every SKU — no more typing the same number a dozen times. Individual rows stay editable afterwards, "Clear all" removes the overrides so pricing falls back to your normal margins, and nothing is written until you hit Save Prices.',
          side: 'bottom',
          nextBtnText: 'Got it',
          onNextClick: () => {
            d.destroy();
            history.push('/search');
          },
        },
      },
    ],
  });
  d.drive();
}

// Per-row print-queue quantity stepper.
export function startQueueQuantityTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-queue-quantity');
  history.push('/tour/products');
}

export function driverQueueQuantity(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-mock-queue-add',
        popover: {
          title: 'Choose How Many Labels to Print',
          description:
            'Tick the products you want, and this bar appears above the results. "Copies each" is how many labels to print per product — type it straight in or nudge it with − and +, and the button tells you the total before you click it. One product or twenty, it is the same control.',
          side: 'bottom',
          nextBtnText: 'Got it',
          onNextClick: () => {
            d.destroy();
            history.push('/search');
          },
        },
      },
    ],
  });
  d.drive();
}

export function startMultiSampleCheckoutTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-multi-sample');
  history.push('/tour/products');
}

// afterDone closes the mock checkout dialog before the tour leaves the page.
export function driverMultiSampleCheckout(history, afterDone) {
  let d;
  // destroy() fires onDestroyed, which is what closes the dialog — that way
  // dismissing the tour early (Esc or the X) cleans up too.
  const finish = () => {
    d.destroy();
    history.push('/search');
  };

  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    onDestroyed: () => {
      if (afterDone) afterDone();
    },
    steps: [
      {
        element: '#tour-mock-multi-sample',
        popover: {
          title: 'Add As Many Samples As They Need (1 of 2)',
          description:
            'The customer is captured once at the top, and the samples collect here underneath. "This product" ticks off every colour they are taking from the board you are already on. "Scan a sample" opens the camera right inside this window, so you can add another board without re-entering their details — anything already added shows as taken.',
          side: 'right',
          nextBtnText: 'Next →',
        },
      },
      {
        element: '#tour-mock-sample-submit',
        popover: {
          title: 'One Press For The Whole Lot (2 of 2)',
          description:
            'However many samples are in the list, this checks them all out to that one customer together — no more repeating the checkout for every board.',
          side: 'top',
          nextBtnText: 'Got it',
          onNextClick: finish,
        },
      },
    ],
  });
  d.drive();
}

export function startDuplicatesTour(history) {
  resumeDuplicateStage(history);
}

// ── Scan Dashboard tour (separate upcoming release — not part of the tour above) ──
// Started automatically when /insights mounts with pendingTour = 'scandashboard'.
export function resumeScanDashboardStage(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-scan-dashboard',
        popover: {
          title: 'Scan Dashboard',
          description:
            'The new Scan Dashboard puts all of your QR scan analytics on one page. Use the filter bar at the top to set the date range, audience (customers vs staff), location, and whether to show only products with a printed QR label (on by default) — every panel below updates together. You get KPIs with trends, a view → like → checkout funnel per product, an activity heatmap, manufacturer share, rising/falling products, and a list of products that haven\'t been scanned yet.',
          side: 'bottom',
          nextBtnText: 'Finish Tour',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-complete');
            history.push('/home');
          },
        },
      },
    ],
  });
  d.drive();
}

// ── 4.8 release tours ──────────────────────────────────────────────────────

// Opens the nav menu and highlights the Feature Requests item; "Take me there"
// lands on the page itself. Menu needs a beat to render before highlighting.
export function startFeatureRequestsTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-feature-requests',
        popover: {
          title: 'Feature Requests',
          description:
            'Have an idea for Showroom Pricing? Vote on features other customers have requested, join the discussion in comments, or submit your own — and watch where each request stands.',
          side: 'left',
          nextBtnText: 'Take me there',
          onNextClick: () => {
            d.destroy();
            setMenuOpen(false);
            history.push('/feature-requests');
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  setTimeout(() => d.drive(), 300);
}

export function startHelpCatalogTour(history, setMenuOpen) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-help',
        popover: {
          title: 'Help Video Catalog',
          description:
            'The Help page now has a searchable library of walkthrough videos. Type what you\'re trying to do and the right video surfaces instantly — new videos appear as they\'re published.',
          side: 'left',
          nextBtnText: 'Take me there',
          onNextClick: () => {
            d.destroy();
            setMenuOpen(false);
            history.push('/about');
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  setTimeout(() => d.drive(), 300);
}

// Navigates to Settings; resumeTimezoneStage runs when the page mounts with
// pendingTour = 'timezone' (same pattern as the QR tour).
export function startTimezoneTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'timezone');
  history.push('/settings');
}

export function resumeTimezoneStage(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: ['next', 'close'],
    steps: [
      {
        element: '#tour-timezone-setting',
        popover: {
          title: 'Company Default Timezone',
          description:
            'Timestamps across the app are shown in this timezone for everyone who hasn\'t chosen their own. Each user can pick a personal timezone on their profile page.',
          side: 'top',
          nextBtnText: 'Got it',
          onNextClick: () => d.destroy(),
        },
      },
    ],
  });
  d.drive();
}

// Price tier mode. Both controls live in Settings > Product Page Settings, so
// navigate there and resume via TOUR_PENDING_KEY (same pattern as the timezone tour).
export function startPriceTierTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'priceTiers');
  history.push('/settings');
}

export function resumePriceTierStage() {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-price-tier-toggle',
        popover: {
          title: 'Show Tiers Instead of Prices (1 of 2)',
          description:
            'Switch this on and customers — including anyone who scans without logging in — see a coloured tier in place of the price. Signed-in staff see the price AND its tier, so you can tell a customer which band something is in without doing the sums. The price never reaches a customer\'s page, so there is nothing for a shopper to dig out of it.',
          side: 'top',
        },
      },
      {
        element: '#tour-price-tiers-btn',
        popover: {
          title: 'Set Up Your Tiers (2 of 2)',
          description:
            'Click Edit Tiers to set the price ranges, labels and colours. Keep one set for the whole company, or give a single store its own. Ranges work the same way as the price colours on your printed QR labels, so a product shows the same tier on screen as it does on its sticker.',
          side: 'top',
          nextBtnText: 'Got it',
          onNextClick: () => d.destroy(),
        },
      },
    ],
  });
  d.drive();
}

// QR print start-position feature. Navigates to the search page (where the Print
// Queue button lives) and resumes via TOUR_PENDING_KEY, same pattern as above.
export function startQrStartPositionTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'qrStartPosition');
  history.push('/search');
}

export function resumeQrStartPositionStage() {
  // Open the Print Queue modal (the button lives on the search page).
  const openBtn = document.getElementById('tour-print-queue');
  if (openBtn) openBtn.click();

  const runDriver = (steps) => {
    let d;
    d = driver({
      allowClose: true,
      showButtons: ['next', 'close'],
      steps: steps.map((s) => ({
        ...s,
        popover: { ...s.popover, nextBtnText: 'Got it', onNextClick: () => d.destroy() },
      })),
    });
    d.drive();
  };

  // The start-position grid only renders once the modal is open AND the queue has
  // items. Poll for it, scroll it into view, then highlight. If it never appears
  // (empty queue), fall back to a centered popover inside the open modal.
  let tries = 0;
  const poll = setInterval(() => {
    tries += 1;
    const picker = document.getElementById('tour-start-position');
    if (picker) {
      clearInterval(poll);
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Let the scroll settle before driver measures the element's position.
      setTimeout(() => {
        runDriver([
          {
            element: '#tour-start-position',
            popover: {
              title: 'Reuse Partially-Printed QR Sheets',
              description:
                "Here's your sticker sheet as a grid. Click the first open label to set where the QR codes start printing — the used stickers before it are skipped, so a half-used sheet doesn't go to waste.",
              side: 'top',
              align: 'center',
            },
          },
        ]);
      }, 450);
    } else if (tries >= 15) {
      // ~3s: modal open but queue empty (grid card not rendered).
      clearInterval(poll);
      runDriver([
        {
          popover: {
            title: 'Reuse Partially-Printed QR Sheets',
            description:
              "Add products to your print queue, then reopen it here — you'll get a grid of the sticker sheet where you can pick which sticker to start on, so a half-used sheet doesn't go to waste.",
          },
        },
      ]);
    }
  }, 200);
}

// Plain-English calculation summary on Admin > Cost Adjustments. Admin.js
// switches to the Cost Adjustments tab when it sees this pending key. The
// summary section only renders for customers on the custom calc graph (and
// after its fetch resolves), so poll for it and fall back to a centered
// popover when it never appears.
export function startCalcSummaryTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'calcSummary');
  history.push('/admin');
}

export function resumeCalcSummaryStage() {
  const runDriver = (steps) => {
    let d;
    d = driver({
      allowClose: true,
      showButtons: ['next', 'close'],
      steps: steps.map((s) => ({
        ...s,
        popover: { ...s.popover, nextBtnText: 'Got it', onNextClick: () => d.destroy() },
      })),
    });
    d.drive();
  };

  let tries = 0;
  const poll = setInterval(() => {
    tries += 1;
    const summary = document.getElementById('tour-calc-summary');
    if (summary) {
      clearInterval(poll);
      // The section is a collapsible accordion — expand it before highlighting.
      const header = document.getElementById('tour-calc-summary-header');
      if (header && header.getAttribute('aria-expanded') !== 'true') header.click();
      summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Let the expand + scroll settle before driver measures the element's position.
      setTimeout(() => {
        runDriver([
          {
            element: '#tour-calc-summary',
            popover: {
              title: 'How Your Prices Are Calculated',
              description:
                'This is your pricing calculation written out step by step — from base cost, through loaded cost, to the retail price — in the exact order it runs. The settings further down this page feed these steps, and a blank setting means its step is skipped. Use the "Pricing model" dropdown to preview the other pricing models available to you, then make one live with "Use This Pricing Model" — your current model is kept so you can switch back.',
              side: 'bottom',
              align: 'center',
            },
          },
        ]);
      }, 600);
    } else if (tries >= 15) {
      // ~3s: customer isn't on the custom calculation yet, so the summary
      // section doesn't render on this site.
      clearInterval(poll);
      runDriver([
        {
          popover: {
            title: 'How Your Prices Are Calculated',
            description:
              'Once your site is on the new calculation engine, the Cost Adjustments page starts with a plain-English, step-by-step summary of exactly how your prices are built.',
          },
        },
      ]);
    }
  }, 200);
}

// Cost Adjustment Rules on Admin > Cost Adjustments. Admin.js switches to the
// Cost Adjustments tab on this pending key; the section is a collapsible
// accordion, so expand it before highlighting.
export function startCostAdjustmentsTour(history) {
  sessionStorage.setItem(TOUR_PENDING_KEY, 'costAdjustments');
  history.push('/admin');
}

export function resumeCostAdjustmentsStage() {
  const runDriver = (steps) => {
    let d;
    d = driver({
      allowClose: true,
      showButtons: ['next', 'close'],
      steps: steps.map((s) => ({
        ...s,
        popover: { ...s.popover, nextBtnText: 'Got it', onNextClick: () => d.destroy() },
      })),
    });
    d.drive();
  };

  let tries = 0;
  const poll = setInterval(() => {
    tries += 1;
    const section = document.getElementById('tour-cost-adjustments');
    if (section) {
      clearInterval(poll);
      const header = document.getElementById('tour-cost-adjustments-header');
      if (header && header.getAttribute('aria-expanded') !== 'true') header.click();
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        runDriver([
          {
            element: '#tour-cost-adjustments',
            popover: {
              title: 'Cost Adjustment Rules',
              description:
                'The cost-landing rules behind your pricing — freight %, added dollars, scope margins — are now yours to manage. Add a rule and pick who it applies to: a manufacturer, distributor, role, material class, sub-class, or size; anything left as "All" matches everything. Where rules overlap, the most specific one wins, and saving warns you about overlaps. The Material Class Margins editor also gains per-class Loaded Margin and Loaded Markup columns.',
              side: 'bottom',
              align: 'center',
            },
          },
        ]);
      }, 600);
    } else if (tries >= 15) {
      clearInterval(poll);
      runDriver([
        {
          popover: {
            title: 'Cost Adjustment Rules',
            description:
              'On Admin > Cost Adjustments you can now manage the cost adjustment rules behind your pricing — freight percentages, added dollars, and scope-specific margins — without contacting support.',
          },
        },
      ]);
    }
  }, 200);
}
